/**
 * Bundle 181 - Exclude/Include ratings modal.
 *
 * Super-admin action to drop a specific survey_response's ratings
 * from public aggregates while preserving everything else on the
 * row (testimonial, missed connections, free-text answers).
 *
 * The dialog is dual-mode:
 *   - mode = "exclude": show reason box, POST to
 *     /admin/survey-responses/{id}/exclude-from-ratings
 *   - mode = "include": show reason box, POST to
 *     /admin/survey-responses/{id}/include-in-ratings
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FilterX, Undo2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const MIN_REASON_LEN = 10;

export function ExcludeRatingsDialog({
  open, mode, row, onOpenChange, onSuccess,
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open, row?.id]);

  const isExcludeMode = mode === 'exclude';
  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen >= MIN_REASON_LEN && !busy;

  const handleSubmit = async () => {
    if (!row?.id) return;
    setBusy(true);
    try {
      const path = isExcludeMode
        ? `/admin/survey-responses/${row.id}/exclude-from-ratings`
        : `/admin/survey-responses/${row.id}/include-in-ratings`;
      const { data } = await api.post(path, { reason: reason.trim() });
      toast.success(isExcludeMode
        ? 'Ratings excluded from public aggregates.'
        : 'Ratings restored to public aggregates.');
      onOpenChange(false);
      if (onSuccess) onSuccess(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Request failed.');
    } finally { setBusy(false); }
  };

  if (!row) return null;

  const overallRating = row?.answers?.overall_rating;
  const gamesRating = row?.answers?.games_rating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="exclude-ratings-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isExcludeMode
              ? 'Exclude ratings from aggregates'
              : 'Include ratings in aggregates'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div
            className="rounded-md border border-neutral-200
                          bg-neutral-50 p-3 text-xs"
            data-testid="exclude-ratings-summary"
          >
            <div className="font-semibold text-primary-900 mb-1">
              {row.recipient_name || '(no name on file)'}
            </div>
            <div className="text-neutral-700 mb-1">
              {row.email || 'no email on file'}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">
                overall_rating: {overallRating ?? 'n/a'}
              </Badge>
              <Badge variant="outline">
                games_rating: {gamesRating ?? 'n/a'}
              </Badge>
              <Badge variant="outline">
                {row.variant_resolved
                  || (row.is_attendee_at_submit
                    ? 'attendee' : 'non-attendee')}
              </Badge>
            </div>
          </div>

          {isExcludeMode ? (
            <div
              className="rounded-md border border-amber-200
                            bg-amber-50 p-3 text-xs text-amber-900
                            flex gap-2"
              data-testid="exclude-ratings-warning"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                The response remains fully preserved. Only the
                ratings from this response are removed from public
                event-rating averages and platform-wide rollups.
                Testimonial, missed connections, and free-text
                feedback are unaffected.
              </div>
            </div>
          ) : (
            <div
              className="rounded-md border border-primary-200
                            bg-primary-50 p-3 text-xs text-primary-900"
              data-testid="include-ratings-warning"
            >
              This response is currently excluded. Restoring it
              will add its ratings back to public event-rating
              averages and platform-wide rollups.
            </div>
          )}

          <div className="space-y-1">
            <label
              className="text-xs font-semibold text-neutral-700"
              htmlFor="exclude-ratings-reason"
            >
              {isExcludeMode
                ? 'Why are we excluding this?'
                : 'Why are we restoring this?'}
              <span className="text-rose-700 ml-1">*</span>
            </label>
            <Textarea
              id="exclude-ratings-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isExcludeMode
                ? 'Explain why (e.g., rating driven by disclosed policy)'
                : 'Explain why we are restoring this'}
              data-testid="exclude-ratings-reason-input"
            />
            <div
              className={
                'text-[10px] '
                + (trimmedLen < MIN_REASON_LEN
                  ? 'text-rose-700' : 'text-neutral-500')
              }
              data-testid="exclude-ratings-reason-counter"
            >
              {trimmedLen}/{MIN_REASON_LEN} characters minimum
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="exclude-ratings-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={
              isExcludeMode
                ? 'bg-rose-700 text-white hover:bg-rose-800'
                : 'bg-primary-800 text-white hover:bg-primary-900'
            }
            data-testid="exclude-ratings-confirm-btn"
          >
            {isExcludeMode ? (
              <>
                <FilterX className="w-4 h-4 mr-2" />
                Exclude ratings
              </>
            ) : (
              <>
                <Undo2 className="w-4 h-4 mr-2" />
                Include ratings
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExcludeRatingsDialog;
